import type { Contact } from '@/modules/contact';
import {
  HYPOTHETICAL_RECIPIENT_NAMES,
  HYPOTHETICAL_SENDER_NAMES,
  HYPOTHETICAL_CONNECTIONS,
} from '@/modules/config';
import {
  PRIMITIVE_NAMES,
  PRIMITIVES,
  type PrimitiveDefinition,
} from '@/modules/form/lang';
import type { PrimitiveName } from '@/modules/template';
import {
  ComposeLogic,
  SlotLogic,
  type SharingFormSlot,
  type SlotAnswers,
} from '@/modules/form';
import type { LegStoryInfoLike } from '@/services/meprfo';
import type { PreviewSegment } from './types';

/**
 * Someone who passed a hypothetical message along: the first name the story
 * carries for them and the first-person connection they picked into the name
 * before theirs.
 */
export interface HypotheticalResharer {
  name: string;
  connection: string;
}

/**
 * Everyone a hypothetical message involves who is not this sender. This
 * sender goes by their own allowed name in the story, where it began.
 */
export interface HypotheticalPeople {
  /** Whoever the message reaches. */
  recipient: Contact;
  /** Whoever sends it — the name it is signed with. */
  senderName: string;
  /**
   * The first-person connection `senderName` picks into the name before
   * theirs, from HYPOTHETICAL_CONNECTIONS like the resharers'.
   */
  senderConnection: string;
  /**
   * The people who passed it along between this sender and `senderName`,
   * nearest this sender first. Empty one degree removed, where the sender is this
   * sender's own recipient.
   */
  resharers: readonly HypotheticalResharer[];
}

// Brackets around a per-recipient primitive's value while a preview composes,
// so the finished text still shows which characters came from the recipient's
// details, and which primitive they came from —
// "Hi \uE000_recipientInformalName\uE002Tyler\uE001," marks "Tyler" as the
// recipient's informal name. Private-use code points: nothing a sender types or
// a form authors contains them, and no step of composing trims, cases or
// collapses them.
const PER_RECIPIENT_OPEN = '\uE000';
const PER_RECIPIENT_NAME_END = '\uE002';
const PER_RECIPIENT_CLOSE = '\uE001';
const PER_RECIPIENT_VALUE = /\uE000([^\uE002]*)\uE002([^\uE001]*)\uE001/;

// A select option that is nothing but a per-sender primitive, as an author
// writes it: `{_profileName}`.
const PER_SENDER_OPTIONS: ReadonlySet<string> = new Set(
  PRIMITIVE_NAMES.filter(name => PRIMITIVES[name].perSender === true).map(
    name => `{${name}}`
  )
);

// How far along HYPOTHETICAL_CONNECTIONS each resharer steps from the one before.
const HYPOTHETICAL_CONNECTION_STRIDE = 5;

function firstNameOf(name: string): string {
  return name.split(' ')[0];
}

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const suffix = ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/**
 * The previews the sheet shows (#447 follow-up): the message as it will be
 * sent, and as it would read one or more degrees removed.
 *
 * A preview degrees removed answers "what would the next person send if they
 * passed this on", and both ends of that message are unknown: the person
 * passing it on has not been written to yet, and whoever they would pick is
 * further off still. So it composes against made-up people rather than against
 * anyone real — which would read as a message to this sender's own recipient,
 * the one thing it is not.
 */
export const PreviewLogic = {
  /**
   * A made-up recipient, named like a contact rather than like a placeholder:
   * the name splits into first and surname, so `{_recipientInformalName}`
   * shortens it ("Hello Tyler") exactly as it shortens a real contact's instead
   * of reading out a full name no template would.
   */
  hypotheticalRecipient(name: string): Contact {
    const [firstName, ...rest] = name.split(' ');
    return {
      localId: 'hypothetical-recipient',
      prefix: '',
      firstName,
      middleName: '',
      lastName: rest.join(' '),
      suffix: '',
      nickname: '',
      tels: [],
      listingCategories: [],
    };
  },

  /**
   * The answers the person passing the message on would give. Where a slot
   * offers a value that names the sender — "Your name" offering the sender's
   * profile name — they answer it with that value, so it names them rather
   * than carrying whatever this sender wrote in for themselves. Every other
   * answer is this sender's, as the message they pass on carries it.
   */
  answersOfTheNextSender(
    form: readonly SharingFormSlot[],
    answers: SlotAnswers
  ): Record<string, string> {
    const next: Record<string, string> = { ...answers };
    for (const slot of ComposeLogic.answerForm(form, answers)) {
      const perSender = SlotLogic.possibleValues(slot).find(value =>
        PER_SENDER_OPTIONS.has(value)
      );
      if (perSender !== undefined) next[slot.id] = perSender;
    }
    return next;
  },

  /**
   * The whole preview for one intent, `degree` degrees removed: pick the
   * made-up people, then compose the message they would send. The caller
   * supplies how this intent's message composes for those people — the facts
   * that change a degree removed are its business, not this module's — or
   * null for an intent with nothing to compose from, which previews as
   * nothing.
   */
  hypotheticalPreview(args: {
    compose: ((people: HypotheticalPeople) => string) | null;
    peopleIndex: number;
    degree: number;
  }): string {
    const { compose, peopleIndex, degree } = args;
    if (!compose) return '';
    return compose(PreviewLogic.hypotheticalPeople(peopleIndex, degree));
  },

  /**
   * Generates a list of made-up people removed from the original sender by `degree`:
   * the recipient, the sender who signs it, and the resharers in between, on
   * the nth look at the preview.
   * The names come from two lists indexed in step, so the sender is never also
   * the recipient, and both wrap — the sheet advances the index as it closes,
   * and names that change each time say they are invented more plainly than a
   * fixed placeholder does.
   *
   * Everyone along the way comes from the sender list in turn, from this
   * sender's own recipient out to whoever sends the message `degree` degrees
   * removed,
   * so each degree is signed by someone else and no made-up name in one story
   * appears twice as long as the list is longer than `degree`. The recipient
   * moves on with them.
   *
   * Each resharer, and then the sender, picks a connection from
   * HYPOTHETICAL_CONNECTIONS, stepping through it by a stride coprime with its
   * length: no connection repeats within a story, neighbouring lines read
   * unrelated, and each look brings a different set.
   */
  hypotheticalPeople(index: number, requestedDegree = 1): HypotheticalPeople {
    const degree = Math.max(requestedDegree, 1);
    const senderAt = (i: number) =>
      HYPOTHETICAL_SENDER_NAMES[i % HYPOTHETICAL_SENDER_NAMES.length];
    const connectionAt = (k: number) =>
      HYPOTHETICAL_CONNECTIONS[
        (index + k * HYPOTHETICAL_CONNECTION_STRIDE) %
          HYPOTHETICAL_CONNECTIONS.length
      ];
    return {
      recipient: PreviewLogic.hypotheticalRecipient(
        HYPOTHETICAL_RECIPIENT_NAMES[
          (index + degree - 1) % HYPOTHETICAL_RECIPIENT_NAMES.length
        ]
      ),
      senderName: senderAt(index + degree - 1),
      senderConnection: connectionAt(degree - 1),
      resharers: Array.from({ length: degree - 1 }, (_, k) => ({
        name: firstNameOf(senderAt(index + k)),
        connection: connectionAt(k),
      })),
    };
  },

  /**
   * Generates the legs (in chronological order) of a hypothetical message's
   * story. The first leg is `ownLeg`, a staged representation of the real
   * sender's leg (to be finalized in the backend when the SMS is confirmed).
   * Then come the made-up resharers drawn from `people`, and last the made-up
   * person whose message is previewed.
   */
  storyLegs(
    people: HypotheticalPeople,
    ownLeg: LegStoryInfoLike
  ): LegStoryInfoLike[] {
    return [
      ownLeg,
      ...people.resharers.map(({ name, connection }) => ({
        allowedSenderName: name,
        connectionToPrevLeg: connection,
      })),
      {
        allowedSenderName: people.senderName,
        connectionToPrevLeg: people.senderConnection,
      },
    ];
  },

  /**
   * The run's reach by the time the message is `degree` degrees removed. Nobody knows
   * how many people each made-up resharer writes to, so each is imagined writing to as
   * many as this sender is — the one number the preview has.
   */
  hypotheticalReach(
    runSoFar: number,
    thisSend: number,
    degree: number
  ): number {
    return runSoFar + thisSend * degree;
  },

  /** What the sheet calls a degree: "How this will share", "2nd reshare". */
  degreeLabel(degree: number): string {
    return degree === 0 ? 'How this will share' : `${ordinal(degree)} reshare`;
  },

  /**
   * Primitive definitions whose per-recipient values are bracketed, so a
   * message composed in the frame they define still shows which parts of the
   * text are the recipient's own details, and which primitive each came from.
   * `segments` reads the brackets back out. An empty value stays empty: there
   * is nothing to show as an example, and an empty primitive composes by its
   * own rules.
   */
  markPerRecipient<F>(
    definitions: readonly PrimitiveDefinition<F>[]
  ): readonly PrimitiveDefinition<F>[] {
    return definitions.map(definition =>
      definition.perRecipient
        ? {
            ...definition,
            bootstrapValue: facts => {
              const value = definition.bootstrapValue(facts);
              return value === ''
                ? ''
                : `${PER_RECIPIENT_OPEN}${definition.name}${PER_RECIPIENT_NAME_END}${value}${PER_RECIPIENT_CLOSE}`;
            },
          }
        : definition
    );
  },

  /**
   * A composed preview cut into pieces, each saying whether it is a
   * per-recipient value that `markPerRecipient` bracketed, and if so, whose
   * primitive it is. Text with no brackets is one plain piece.
   */
  segments(text: string): readonly PreviewSegment[] {
    // Splitting on the two-group pattern interleaves plain text with each
    // bracketed value's primitive name and value: [text, name, value, text, …].
    const pieces = text.split(PER_RECIPIENT_VALUE);
    const segments: PreviewSegment[] = [];
    for (let i = 0; i < pieces.length; i += 3) {
      if (pieces[i] !== '') {
        segments.push({ text: pieces[i], perRecipient: false });
      }
      if (i + 2 < pieces.length) {
        segments.push({
          text: pieces[i + 2],
          perRecipient: true,
          primitive: pieces[i + 1] as PrimitiveName,
        });
      }
    }
    return segments;
  },
};
